import { ReactNode } from "react";
import { AUTH_METHOD, IDS } from "./enums";

export type Medium = "email" | "sms" | "biometric";
export type ILang = "he-IL" | "en-US";

/** ISO 4217 codes for `Intl.NumberFormat` / UICurrency */
export enum Currency {
  ILS = "ILS",
  USD = "USD",
}

// API Request Types
export interface GetReq {
  fullUrl?: string;
  url?: string;
  hideLoader?: boolean;
}

export interface PostReq {
  fullUrl?: string;
  url?: string;
  body?: any;
  hideLoader?: boolean;
}

// Headers Type
export interface Headers {
  Accept?: string;
  "Content-Type"?: string;
  Authorization?: string;
  [key: string]: string | undefined;
}

// HeadersInit type for fetch API compatibility
export type HeadersInit = Headers | Record<string, string> | [string, string][];

// Response Messages Enum
export enum ResponseMessages {
  OTP_INVALID = "OTP_INVALID",
  USER_BLOCKED = "USER_BLOCKED",
  // Add other response messages as needed
}
export type TextElements = "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "span";
export interface UITextProps {
  id?: IDS;
  size: number;
  weight: number;
  style?: string;
  text: ReactNode;
  isAriaHidden?: boolean;
  tag?: TextElements;
  ariaLabel?: string;
  announce?: boolean;
  politeness?: "polite" | "assertive";
  tabIndex?: number;
}

// Initial Data Response Types
export interface Testimonial {
  id: string;
  text: string;
  rating: number;
  location: string;
  customerName: string;
}

export interface FAQItem {
  id: string;
  answer: string;
  question: string;
}

export interface TsInitialDataResponse {
  maxUsageNoRiskAmountIl: number;
  maxUsageRiskAmountIl: number;
  minRemainingUsageAmountIl: number;
  testimonials: Testimonial[];
  faqItems: FAQItem[];
  tsValidateCVV?: boolean;
  tsDiscountPct?: number;
  /** ILS per 1 USD; used to convert flight savings (USD price × discount %) to ₪. */
  usdExchangeRate?: number;
  /** Same as `usdExchangeRate` when the API returns PascalCase. */
  USDExchangeRate?: number;
  /** Basis for rounding-down customer remaining usage when displaying it in the UI (e.g. 100 -> 26,157 displays as 26,100). Event logs still send the exact amount. */
  tsRemainingUsageRoundBasis?: number;
}

// Affiliates Response Types
export interface Website {
  id: string;
  name: string;
  logo: string;
  short_desc: string;
  link_text: string;
  /** Present on some get-affiliates rows (e.g. IMPACT for flights). */
  aggregatorName?: string;
}

export interface WebsiteClicks {
  id: string;
  name: string;
  logo: string;
  short_desc: string;
  link_text: string;
  affiliateTimer: number;
  /** Active click id for extend-ts-click-timer (from get-affiliates-timers). */
  feClickId?: string;
  /** e.g. IMPACT (flights hero), TravelPayouts (hotels/attractions hero) — from get-affiliates-timers. */
  aggregatorName?: string;
  /** Outbound URL stored from the original click (from get-affiliates-timers, WI #227707). */
  outbound_url?: string;
  /** Click end date from get-affiliates-timers (for repeat-visit analytics). */
  click_end_date?: string;
  /** Card closing date from get-affiliates-timers (for repeat-visit analytics). */
  card_closing_date?: string;
}
export interface Affiliate {
  type: string;
  websites: Website[];
}
export interface AffiliateClicks {
  type: string;
  websites: WebsiteClicks[];
}

/** Per-aggregator countdown from qry/settings/get-affiliates-timers (`affiliatesTimers`). */
export interface AffiliateAggregatorTimer {
  aggregatorName: string;
  aggregatorTimer: number;
}

/** Row from get-affiliates linking aggregator to id (e.g. IMPACT for flights). */
export interface TsAffiliatesAggregatorRow {
  id: string;
  aggregatorName: string;
}

export interface TsAffiliatesResponse {
  affiliates: Affiliate[];
  /** When API returns a flat list of aggregators (optional; else use `aggregatorName` on `Website`). */
  aggregators?: TsAffiliatesAggregatorRow[] | null;
}

/** Response from get-affiliates-timers: optional website-level `affiliates` + top-level `affiliatesTimers`. */
export interface TsAffiliatesResponseClicks {
  affiliates?: AffiliateClicks[] | null;
  affiliatesTimers?: AffiliateAggregatorTimer[] | null;
}

// Translations Response Types
export interface TranslationsResponse {
  responseMessage: string;
  responseCode: string;
  responseData: {
    strings: Record<string, string>;
  };
}

// Server translations response format (raw response from API)
// Format: {"he-IL": {key: "value"}, "en-US": {key: "value"}}
export type ServerTranslationsResponse = Record<ILang, Record<string, string>>;

// Send OTP Response Types
export interface SendOtpResponseMetadata {
  httpStatusCode: number;
  requestId: string;
  attempts: number;
  totalRetryDelay: number;
}

export interface SendOtpResponse {
  $metadata: SendOtpResponseMetadata;
  ChallengeName: string;
  ChallengeParameters: {
    challenge: string;
    medium: Medium;
  };
  Session: string;
}

// Validate OTP Response Types
export interface ValidateOtpResponseMetadata {
  httpStatusCode: number;
  requestId: string;
  attempts: number;
  totalRetryDelay: number;
}

export interface AuthenticationResult {
  AccessToken: string;
  ExpiresIn: number;
  IdToken: string;
  RefreshToken: string;
  TokenType: string;
}

/** Request for POST /cmd/auth/refresh-jwt */
export interface RefreshJwtRequest {
  refreshToken: string;
}

/** Success response (200) from POST /cmd/auth/refresh-jwt */
export interface RefreshJwtResponse {
  AccessToken: string;
  IdToken: string;
  ExpiresIn: number;
  TokenType: string;
  authExpiresAt: number;
}

export interface ValidateOtpResponse {
  $metadata: ValidateOtpResponseMetadata;
  AuthenticationResult?: AuthenticationResult;
  ChallengeName?: string;
  ChallengeParameters?: Record<string, unknown>;
  Session?: string;
  user_token?: string;
  businessUnit?: string;
  authExpiresAt?: number;
  /** Encrypted customer ID from pcil_customers.customer_id */
  customer_id_encrypted?: string;
}

/** Request for POST /cmd/clicks/save-ts-click */
export interface SaveTsClicksInput {
  fePersonalId: string;
  feAffiliateId: number;
  feAffiliateType: string;
  feCampaignKey: string;
  feAgentId: string;
  feRemainingUsageAmount: number;
  feUtmSource?: string;
  feUtmMedium?: string;
  feVariation?: string;
  feCidEnc?: string;
}

/** Response from save-ts-click; use `feClickId` on save-ts-flight for flights outbound. */
export interface SaveTsClicksResponse {
  feAffiliateUrl?: string;
  /** New click id when save succeeded */
  feClickId?: string;
  feClickEndDate?: string;
  feCardClosingDate?: string;
  /** 0 = record created; 1 = error */
  returnCode?: number;
  returnMessage?: string;
  /** @deprecated prefer `feClickId` */
  feFlightSharedId?: string;
  /** @deprecated prefer `feClickId` */
  clickId?: string;
  /** PascalCase from some JSON contracts */
  ClickId?: string;
}

/** Resolves click id from save-ts-click (canonical `feClickId`, then legacy aliases). */
export function getSaveTsClickId(
  response: SaveTsClicksResponse | null | undefined
): string {
  if (!response) return "";
  const raw =
    response.feClickId ?? response.clickId ?? response.ClickId ?? "";
  const s = String(raw).trim();
  return s;
}

/** Request for POST /cmd/clicks/extend-ts-click-timer */
export interface ExtendTsClickTimerInput {
  fePersonalId: string;
  feAffiliateId: number;
  feClickId: string;
}

/** Response from extend-ts-click-timer (same shape as get-affiliates-timers + returnCode/returnMessage + feTimer) */
export interface ExtendTsClickTimerResponse {
  /** 0 = record updated; 1 = error */
  returnCode: number;
  returnMessage: string;
  /** New extended timer (ms) for the specific affiliate that was extended */
  feTimer?: number;
  /** Updated affiliates with new timers per website */
  affiliates?: AffiliateClicks[] | null;
  /** Updated aggregator-level timers */
  affiliatesTimers?: AffiliateAggregatorTimer[] | null;
}

/** One segment row for POST /cmd/flights/save-ts-flight `feFlightSegments` */
export interface SaveTsFlightSegmentRow {
  flightNumber: string;
  from: string;
  to: string;
  from_heb?: string;
  to_heb?: string;
}

export interface SaveTsFlightSegmentsPayload {
  outbound: SaveTsFlightSegmentRow[];
  inbound: SaveTsFlightSegmentRow[];
}

export interface SaveTsFlightsInput {
  fePersonalId: string;
  feAffiliateId: number;
  feCampaignKey: string;
  feRemainingUsageAmount: number;
  /** Flight agent outbound click (optional; hotels flow omits these) */
  feClickId?: string;
  feFlightPriceAtClick?: number;
  feFlightCurrency?: string;
  feFlightOrigin?: string;
  feFlightDestination?: string;
  feFlightFromDate?: string;
  feFlightToDate?: string;
  feFlightAdults?: number;
  feFlightChildren?: number;
  feFlightChildrenAges?: number[];
  /** e.g. Skyscanner agent key: alic, bfil */
  feFlightSupplier?: string;
  feOutboundUrl?: string;
  /** Per-leg segment list (carrier displayCode + marketing flight number, IATA endpoints) */
  feFlightSegments?: SaveTsFlightSegmentsPayload;
  feFlightOriginHeb?: string;
  feFlightDestinationHeb?: string;
}
/**
 * Response from POST /cmd/flights/save-ts-flight.
 * Success shape (stable): `{ feFlightSharedId, returnCode: 0, returnMessage }`.
 */
export interface SaveTsFlightsResponse {
  returnCode: number;
  returnMessage: string;
  /** Appended to outbound deep link as query param `sharedId` */
  feFlightSharedId?: string;
  feAffiliateUrl?: string;
}

// Check TS Eligibility Response Types
export interface CheckTsEligibilityResponse {
  feRemainingUsageAmountIl: number;
  feUsageAmountIl: number;
  returnCode: number;
  returnMessage: string;
  feCustomerFirstName?: string;
  feGroupMailingConfirmation?: boolean;
  feUsageLimitReleaseDate?: string;
  /** Encrypted customer id; forwarded to analytics as `general.customer_id_encrypted`. */
  feCustomerId?: string;
}

export interface GetTsPaymentMethodResponse {
  returnCode: number;
  returnMessage: string;
  feCc4LastDigits?: string;
}

export interface UpdateGroupMailingConfirmationInput {
  feGroupMailingConfirmation: boolean;
}

export interface UpdateGroupMailingConfirmationResponse {
  returnCode: number;
  returnMessage: string;
}

export interface EventLog {
  event_name: string;
  event_screen: string;
  status?: string;
  failure_reason?: unknown;
  auth?: AnalyticsEventAuthDetails;
  affiliate?: AnalyticsEventAffiliateDetails;
  flight?: AnalyticsEventFlightDetails;
  flight_supplier?: AnalyticsEventFlightSupplierDetails;
  link?: AnalyticsEventLinkDetails;
  auth_method?: AUTH_METHOD;
}

export interface AnalyticsEventGeneral {
  event_name: string;
  event_screen: string;
  status: string;
  failure_reason: string;
  event_timestamp: string;
  app_version: string;
  device_type: string;
  os_version: string;
  user_token: string;
  business_unit: string;
  user_platform: string;
  channel: string;
  app_session_id: string;
  ip: string;
  /** Encrypted customer id from check-ts-eligibility. */
  customer_id_encrypted: string;
  /** UTM params captured from the landing URL. */
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  /** A/B variation captured from the landing URL. */
  variation: string;
  /** GA4 session id as sent to Google Analytics. */
  ga_session_id: string;
}
export type AnalyticsEventAuthDetails = {
  auth_method?: AUTH_METHOD;
  auth_phone?: string;
  auth_email?: string;
};

export type AnalyticsEventAffiliateDetails = {
  affiliate_type?: string;
  affiliate_id?: string;
  affiliate_name?: string;
  affiliate_url?: string;
  sub_id?: string;
  click_id?: string;
  click_date?: string;
  click_end_date?: string;
  card_closing_date?: string;
  agent_id?: string;
  campaign_code?: string;
  UTM_params?: string;
  remaining_usage_amount_il?: number;
  usage_limit_release_date?: string;
};

export type AnalyticsEventFlightDetails = {
  flight_origin?: string;
  flight_destination?: string;
  flight_from_date?: string;
  flight_to_date?: string;
  flight_adults?: number;
  flight_children?: number;
  flight_children_ages?: number[];
};

export type AnalyticsEventFlightSupplierDetails = {
  flight_shared_id?: string;
  flight_price_at_click?: string;
  flight_currency?: string;
  flight_sorting_option?: string;
  flight_number?: string;
  flight_segments?: SaveTsFlightSegmentsPayload;
  flight_supplier?: string;
  flight_supplier_number?: string;
  additional_suppliers?: string[];
  outbound_url?: string;
};

export type AnalyticsEventLinkDetails = {
  link_url?: string;
  link_name?: string;
};

export interface AnalyticsEvent {
  general: AnalyticsEventGeneral;
  auth?: AnalyticsEventAuthDetails;
  affiliate?: AnalyticsEventAffiliateDetails;
  flight?: AnalyticsEventFlightDetails;
  flight_supplier?: AnalyticsEventFlightSupplierDetails;
  link?: AnalyticsEventLinkDetails;
}
export interface GetCreditGuardIframeUrlInput {
  failureRedirectUrl: string;
  successRedirectUrl: string;
  jsonConfig: Record<string, unknown>;
}

/** Normalized iframe URL returned by getCreditGuardIframeUrl thunk */
export interface GetCreditGuardIframeUrlResponse {
  url: string;
  txId?: string;
  /** Returned instead of `url` when the backend detects a payment-method issue (WI #227724). */
  failureRedirectUrl?: string;
}

export type InquireGCIframeResultsInput = Record<string, unknown>;

export interface InquireGCIframeResultsResponse {
  responseCode?: string;
  responseMessage?: string;
  [key: string]: unknown;
}